import * as React from 'react';
import {
    Alert, Box, Card, CardContent, Chip, Grid, Link as MuiLink, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AI_LABELS, getAiConversations } from '../../Api';
import { AiConversationDefaultsDto, AiConversationSummaryDto } from '../../models/dtos/AiDto';
import type { AgentForm } from '../../Pages/ai/AiAgentEditPage';
import { aiLoadErrorText, formatAiDate } from './AiShared';

const digits = (value: string) => value.replace(/[^\d]/g, '');

/**
 * Agent editor › Conversations: how the agent remembers a thread (conversation mode: a subject and an
 * externalMessageId on every message, e.g. Betty per support chat). Blank fields use the hub's defaults.
 */
export default function AgentConversationSettings({ form, patch, defaults, agentCode, dirty }: {
    form: AgentForm;
    patch: (p: Partial<AgentForm>) => void;
    defaults: AiConversationDefaultsDto | null;
    agentCode: string;
    dirty: boolean;
}) {
    const fallback = (value: number | string | undefined, suffix = '') => (value === undefined ? 'hub default' : `default: ${value}${suffix}`);
    return (
        <Stack gap={2}>
            {dirty && <Alert severity="info">Save to apply the changes (the button is at the top).</Alert>}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6">Conversation memory</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Used when a caller runs the agent in conversation mode (a subject and an id on every message — e.g. Betty per
                        support chat): every message is stored once, the agent's tool calls and replies are replayed on the next run,
                        older parts are folded into a summary. Blank fields use the hub's defaults.
                        {defaults ? ` Conversations are deleted ${defaults.retentionDays} days after their last message.` : ''}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Max runs per conversation" fullWidth value={form.maxConversationRuns}
                                onChange={e => patch({ maxConversationRuns: digits(e.target.value) })}
                                helperText="Blank = unlimited. Beyond it the run is refused (CONVERSATION_LIMIT_REACHED) and messaging hands the chat to a human." />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Context budget (tokens)" fullWidth value={form.contextTokens}
                                placeholder={defaults ? String(defaults.contextTokens) : ''}
                                onChange={e => patch({ contextTokens: digits(e.target.value) })}
                                helperText={`Replayed transcript size (≈ 4 characters per token); beyond it the oldest part is summarized. Min 2000, ${fallback(defaults?.contextTokens)}.`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Earlier tool results (characters)" fullWidth value={form.replayToolResultChars}
                                placeholder={defaults ? String(defaults.replayToolResultChars) : ''}
                                onChange={e => patch({ replayToolResultChars: digits(e.target.value) })}
                                helperText={`Tool results of earlier runs are shortened to this when replayed. Min 200, ${fallback(defaults?.replayToolResultChars)}.`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Other conversations note (days)" fullWidth value={form.otherConversationsDays}
                                placeholder={defaults ? String(defaults.otherConversationsDays) : ''}
                                onChange={e => patch({ otherConversationsDays: digits(e.target.value) })}
                                helperText={`The first turn lists the same user's other conversations of this period (summary + actions taken). 0 = off, ${fallback(defaults?.otherConversationsDays, ' days')}.`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Photos shown (newest)" fullWidth value={form.replayImages}
                                placeholder={defaults?.replayImages != null ? String(defaults.replayImages) : ''}
                                onChange={e => patch({ replayImages: digits(e.target.value) })}
                                helperText={`Photo lines in the user's messages (e.g. support screenshots) go to the model as images: the newest this many on each run, older ones as a note. 0 = none, ${fallback(defaults?.replayImages)}.`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Summary task" fullWidth value={form.summaryTask}
                                placeholder={defaults?.summaryTask ?? ''}
                                onChange={e => patch({ summaryTask: e.target.value.trim() })}
                                helperText={<>Task that folds old parts into the summary (variables summary, transcript). {defaults
                                    ? <MuiLink component={RouterLink} to={`/ai/tasks/${encodeURIComponent(form.summaryTask || defaults.summaryTask)}`}>Open task</MuiLink>
                                    : null}</>} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <RecentConversations agentCode={agentCode} />
        </Stack>
    );
}

function RecentConversations({ agentCode }: { agentCode: string }) {
    const navigate = useNavigate();
    const [rows, setRows] = React.useState<AiConversationSummaryDto[] | null>(null);
    const [total, setTotal] = React.useState(0);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        getAiConversations({ agentCode }, 0, 10).then(page => {
            if (cancelled) return;
            if (page == null) {
                setError(aiLoadErrorText(AI_LABELS.conversations, 'Failed to load conversations.'));
                setRows([]);
                return;
            }
            setRows(page.items ?? []);
            setTotal(page.totalItems ?? 0);
        });
        return () => { cancelled = true; };
    }, [agentCode]);

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Recent conversations {total > 0 ? `(${total})` : ''}</Typography>
                    <MuiLink component={RouterLink} to="/ai/conversations" underline="hover">All conversations</MuiLink>
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                {rows != null && rows.length === 0 && !error && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No conversations yet.</Typography>
                )}
                {rows != null && rows.length > 0 && (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Last message</TableCell>
                                    <TableCell>Thread</TableCell>
                                    <TableCell>End user</TableCell>
                                    <TableCell>Items</TableCell>
                                    <TableCell>Summary</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow key={row.conversationId} hover sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/ai/conversations/${encodeURIComponent(row.conversationId)}`)}>
                                        <TableCell>{formatAiDate(row.lastMessageAt)}</TableCell>
                                        <TableCell>{row.subjectType} / {row.subjectId}</TableCell>
                                        <TableCell>{row.endUserId != null ? `#${row.endUserId}` : '—'}</TableCell>
                                        <TableCell>{row.messageCount}</TableCell>
                                        <TableCell>{row.hasSummary ? <Chip size="small" label="yes" variant="outlined" color="info" /> : null}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
